/** EditAvatarModal — edit name, language and look (via gpt-image-2 image edit). */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Sparkles } from "lucide-react";
import { ugcStudioApi, type UgcAvatar } from "./api";

const LANGUAGE_OPTIONS = [
  "Polish",
  "English (American)",
  "English (British)",
  "German",
  "Spanish (Castilian)",
  "Spanish (Latin American)",
  "French",
  "Italian",
  "Portuguese (Brazilian)",
  "Czech",
  "Slovak",
  "Ukrainian",
  "Russian",
] as const;

const EDIT_LOOK_TOKEN_COST = 800;

interface EditAvatarModalProps {
  open: boolean;
  avatar: UgcAvatar | null;
  onClose: () => void;
  onSaved: () => void;
}

export function EditAvatarModal({ open, avatar, onClose, onSaved }: EditAvatarModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [language, setLanguage] = useState<string>("Polish");
  const [editPrompt, setEditPrompt] = useState("");
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!avatar) return;
    setName(avatar.name);
    const lang = avatar.appearanceMeta?.spokenLanguage;
    setLanguage(typeof lang === "string" && lang.length > 0 ? lang : "Polish");
    setEditPrompt("");
    setPreviewUrl(null);
  }, [avatar]);

  const saveMut = useMutation({
    mutationFn: () => {
      if (!avatar) throw new Error("No avatar");
      return ugcStudioApi.updateAvatar(avatar.id, { name: name.trim(), spokenLanguage: language });
    },
    onSuccess: () => {
      onSaved();
      onClose();
      toast({ title: t("ugcStudio.avatar.updated") });
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.avatar.saveError"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const editLookMut = useMutation({
    mutationFn: () => {
      if (!avatar) throw new Error("No avatar");
      return ugcStudioApi.editAvatarLook(avatar.id, editPrompt.trim());
    },
    onSuccess: (res) => {
      setPreviewUrl(res.avatar.signedUrl);
      onSaved();
      toast({
        title: t("ugcStudio.avatar.appearanceUpdated"),
        description: t("ugcStudio.avatar.appearanceUpdatedDesc", {
          tokens: res.tokensRemaining.toLocaleString(),
        }),
      });
    },
    onError: (err: Error) =>
      toast({
        title: t("ugcStudio.avatar.editFailed"),
        description: err.message,
        variant: "destructive",
      }),
  });

  const canSave = !!avatar && name.trim().length > 0 && !saveMut.isPending;
  const canEditLook = !!avatar && editPrompt.trim().length >= 3 && !editLookMut.isPending;
  const displayedImage = previewUrl ?? avatar?.signedUrl;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="bg-[#0a0c10] border-white/[0.08] max-w-lg">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">{t("ugcStudio.avatar.editTitle")}</h3>
        </div>

        {avatar && (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-16 h-20 rounded-lg overflow-hidden bg-white/[0.04] flex-shrink-0 relative">
                {displayedImage && (
                  <img
                    src={displayedImage}
                    alt={avatar.name}
                    className="w-full h-full object-cover"
                  />
                )}
                {editLookMut.isPending && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                  </div>
                )}
              </div>
              <div className="text-white/50 text-xs">{t("ugcStudio.avatar.editDescription")}</div>
            </div>

            <div>
              <label className="text-white/60 text-xs mb-1 block">
                {t("ugcStudio.avatar.editNameLabel")}
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={80}
                className="bg-white/[0.04] border-white/[0.08] text-white"
              />
            </div>

            <div>
              <label className="text-white/60 text-xs mb-1 block">
                {t("ugcStudio.avatar.editLanguageLabel")}
              </label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:border-white/20"
              >
                {LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt} className="bg-[#0a0c10]">
                    {opt}
                  </option>
                ))}
              </select>
              <p className="text-white/40 text-[11px] mt-1">
                {t("ugcStudio.avatar.editLanguageHint")}
              </p>
            </div>

            <div className="pt-3 border-t border-white/[0.06]">
              <label className="text-white/60 text-xs mb-1 block">
                {t("ugcStudio.avatar.editLookLabel")}
              </label>
              <Textarea
                value={editPrompt}
                onChange={(e) => setEditPrompt(e.target.value)}
                placeholder={t("ugcStudio.avatar.editLookPlaceholder")}
                maxLength={1000}
                className="min-h-[80px] bg-white/[0.04] border-white/[0.08] text-white text-sm resize-none"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-white/40 text-[11px]">{t("ugcStudio.avatar.editLookHint")}</p>
                <span className="text-white/40 text-[11px] tabular-nums">
                  {editPrompt.length} / 1000
                </span>
              </div>
              <Button
                onClick={() => editLookMut.mutate()}
                disabled={!canEditLook}
                className="w-full mt-2 bg-white/[0.06] hover:bg-white/[0.1] text-white border border-white/[0.08]"
                variant="ghost"
              >
                {editLookMut.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {t("ugcStudio.avatar.editLookButton", { cost: EDIT_LOOK_TOKEN_COST })}
              </Button>
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="ghost"
                onClick={onClose}
                className="flex-1 text-white/70 hover:text-white hover:bg-white/[0.06]"
              >
                {t("ugcStudio.avatar.closeButton")}
              </Button>
              <Button
                onClick={() => saveMut.mutate()}
                disabled={!canSave}
                className="flex-1 bg-lime-400 hover:bg-lime-300 text-black"
              >
                {saveMut.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {t("ugcStudio.avatar.saveButton")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
